import classNames from 'classnames';
import {
	CompositeDecorator,
	ContentBlock,
	ContentState,
	DraftEditorCommand,
	Editor,
	EditorState,
	getDefaultKeyBinding,
	Modifier,
	RichUtils,
	SelectionState
} from 'draft-js';
import { stateToHTML } from 'draft-js-export-html';
import { stateFromHTML } from 'draft-js-import-html';
import 'draft-js/dist/Draft.css';
import React, {
	HTMLAttributes,
	PropsWithChildren,
	useEffect,
	useRef,
	useState
} from 'react';
import type {
	ContextMenuHandler,
	MouseHandler
} from '../ContextMenu/ContextMenu';
import './RichEditor.scss';

const styleMap = {
	'header-one': {
		fontSize: '30px',
		color: 'blue'
	}
};

const getBlockStyle = (block: ContentBlock): string => {
	switch (block.getType()) {
		case 'header-one':
			return 'RichEditor-header-one';
		default:
			return '';
	}
};

export type DraftEditor = Editor & { editorContainer: HTMLDivElement };

type Props = Omit<HTMLAttributes<HTMLElement>, 'onChange'> & {
	readOnly: boolean;
	onChange: (message: string) => void;
	value: string;
	onContextMenu?: ContextMenuHandler;
	autoFocus?: boolean;
	// showContextMenu?: boolean
	contextMenuValue?: string;
	refreshContextMenu?: boolean;
	disabled?: boolean;
	onFocus?: (e: React.FocusEvent<HTMLElement, Element>) => void;
	onBlur?: () => void;
	enablePropagation?: boolean;
	disableDisabledStyles?: boolean;
	removeHoverStyle?: boolean;
};

const RichEditor = React.forwardRef<DraftEditor, Props>(
	(
		{
			onChange,
			value,
			readOnly,
			className,
			onContextMenu,
			autoFocus = false,
			// showContextMenu = false,
			contextMenuValue,
			refreshContextMenu = false,
			disabled = false,
			onFocus,
			onBlur,
			enablePropagation = false,
			disableDisabledStyles = false,
			removeHoverStyle = false
		},
		ref
	): JSX.Element => {
		const editor = useRef<DraftEditor>(null);
		const refToUse = ref || editor;
		const variables: RegExp[] = [/\[business_name\]/g, /\[list_name\]/g];

		const Decorated = ({
			children
		}: PropsWithChildren<null>): JSX.Element => {
			return <span className="highlighted">{children}</span>;
		};

		function findWithRegex(
			words: RegExp[],
			contentBlock: ContentBlock,
			callback: (start: number, end: number) => void
		) {
			const text = contentBlock.getText();
			words.forEach((word: RegExp) => {
				const matches = [...text.matchAll(word)];
				matches.forEach(match =>
					callback(
						match.index as number,
						(match.index as number) + match[0].length
					)
				);
			});
		}

		function handleStrategy(
			contentBlock: ContentBlock,
			callback: (start: number, end: number) => void
		) {
			findWithRegex(variables, contentBlock, callback);
		}

		const createDecorator = () =>
			new CompositeDecorator([
				{
					strategy: handleStrategy,
					component: Decorated
				}
			]);

		const [editorState, setEditorState] = useState<EditorState>(
			EditorState.createWithContent(
				stateFromHTML(value ?? ''),
				createDecorator()
			)
		);

		const [editorFocus, setEditorFocus] = useState<boolean>(false);

		const focus = (e: React.FocusEvent<HTMLElement, Element>) => {
			editor.current?.focus();

			setEditorFocus(true);

			if (onFocus) {
				onFocus(e);
			}
		};

		const blur = () => {
			setEditorFocus(false);

			if (onBlur) {
				onBlur();
			}
		};

		const handleKeyCommand = (
			command: DraftEditorCommand,
			eState: EditorState
		) => {
			const newState = RichUtils.handleKeyCommand(eState, command);
			if (newState) {
				setEditorState(newState);

				return 'handled';
			}
			return 'not-handled';
		};

		const mapKeyToEditorCommand = (e: React.KeyboardEvent) => {
			switch (e.code.toLowerCase()) {
				case 'tab': {
					const newEditorState = RichUtils.onTab(e, editorState, 4);

					if (newEditorState !== editorState) {
						setEditorState(newEditorState);
					}

					return null;
				}
				default:
					return getDefaultKeyBinding(e);
			}
		};

		const editorContent = editorState.getCurrentContent();
		const editorHTMLContent = stateToHTML(editorContent);

		let csName = 'RichEditor-editor';
		if (!editorContent.hasText()) {
			if (editorContent.getBlockMap().first().getType() !== 'unstyled') {
				csName += ' RichEditor-hidePlaceholder';
			}
		}

		useEffect(() => {
			if (value !== editorHTMLContent) {
				onChange(editorHTMLContent);
			}
		}, [editorHTMLContent]);

		useEffect(() => {
			if (autoFocus || (editorHTMLContent !== value && !readOnly)) {
				focus();
				const newEditorState = EditorState.createWithContent(
					stateFromHTML(value || ''),
					createDecorator()
				);

				setEditorState(EditorState.moveFocusToEnd(newEditorState));
			}
		}, [value, autoFocus]);

		useEffect(() => {
			if (contextMenuValue) {
				editor.current?.focus();

				const selectionState = editorState.getSelection();
				const anchorKey = selectionState.getAnchorKey();
				const start = selectionState.getStartOffset();

				const newSelectionState = SelectionState.createEmpty(anchorKey);
				const updatedSelection = newSelectionState.merge({
					focusOffset: start,
					anchorOffset: start
				});

				const newContentState = Modifier.insertText(
					editorState.getCurrentContent(),
					updatedSelection,
					contextMenuValue
				);

				const newEditorState = EditorState.createWithContent(
					newContentState,
					createDecorator()
				);

				setEditorState(
					EditorState.forceSelection(
						newEditorState,
						newContentState.getSelectionAfter()
					)
				);

				onChange(stateToHTML(newContentState));
			}
		}, [contextMenuValue, refreshContextMenu]);

		useEffect(() => {
			if (editor.current) {
				const element = editor.current.editorContainer;

				element.addEventListener(
					'contextmenu',
					onContextMenu as MouseHandler
				);

				if (!enablePropagation) {
					element.addEventListener('click', e => e.stopPropagation());
				}
			}

			return () => {
				if (editor.current) {
					const element = editor.current.editorContainer;

					element.removeEventListener(
						'contextmenu',
						onContextMenu as MouseHandler
					);

					element.removeEventListener('click', e =>
						e.stopPropagation()
					);
				}
			};
		}, [editor.current]);

		useEffect(() => {
			// @ts-ignore
			if (refToUse.current?.editor) {
				// @ts-ignore
				refToUse.current.editor.id = 'email-editor';

				const classElements = document.getElementsByClassName(
					'public-DraftStyleDefault-block public-DraftStyleDefault-ltr'
				);

				for (let i = 0; i < classElements.length; i++) {
					classElements[i].id = 'email-editor';
				}

				const elements =
					// @ts-ignore
					refToUse.current.editor.getElementsByTagName('span');
				for (let i = 0; i < elements.length; i++) {
					elements[i].id = 'email-editor';
				}
			}
		}, [editorState]);

		const handlePastedText = (text?: string) => {
			if (text) {
				const currentContent = editorState.getCurrentContent();
				const selection = editorState.getSelection();

				const newContent = Modifier.replaceText(
					currentContent,
					selection,
					text
				);

				const newEditorState = EditorState.push(
					editorState,
					newContent,
					'insert-fragment'
				);

				setEditorState(
					EditorState.forceSelection(
						newEditorState,
						newContent.getSelectionAfter()
					)
				);

				return 'handled'; // Prevent default behavior
			}

			return 'not-handled';
		};

		return (
			<div
				className={classNames(
					className,
					'z-0',
					'RichEditor-root',
					{
						'RichEditor-root--disabled':
							disabled && !disableDisabledStyles
					},
					{ 'has-focus': editorFocus && !disableDisabledStyles },
					{ 'remove-hover-style': removeHoverStyle }
				)}
			>
				<div
					className={classNames(className, csName)}
					role="presentation"
				>
					<Editor
						readOnly={disabled || readOnly}
						ref={refToUse}
						spellCheck
						onFocus={focus}
						onBlur={blur}
						blockStyleFn={getBlockStyle}
						customStyleMap={styleMap}
						editorState={editorState}
						onChange={setEditorState}
						placeholder="Your message..."
						handleKeyCommand={handleKeyCommand}
						keyBindingFn={mapKeyToEditorCommand}
						handlePastedText={handlePastedText}
					/>
				</div>
			</div>
		);
	}
);

export default RichEditor;
